import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from '../config/axiosConfig';
import {
    useTheme,
    Typography,
    Alert,
    Container,
    FormControl,
    InputLabel,
    Input,
    Button,
    Box,
    Paper,
    InputAdornment,
    IconButton,
    GlobalStyles,
    alpha
} from '@mui/material';
import {
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import logo from '../images/rodeoLogo.png';

const ResetPassword = () => {
    const theme = useTheme();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!newPassword || !confirmPassword) {
            setError('All fields are required');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post('/reset-password', { token, newPassword });
            setMessage(response.data.message);
        } catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const inputSx = {
        backgroundColor: 'transparent',
        transition: 'all 0.3s ease-in-out',
        '&:hover': { backgroundColor: 'transparent' },
        '&.Mui-focused': { backgroundColor: 'transparent' }
    };

    if (!token) {
        return (
            <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: { xs: 4, md: 8 } }}>
                <Paper elevation={0} sx={{
                    width: '100%', borderRadius: 2, p: { xs: 3, sm: 4, md: 6 }, textAlign: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                    <Alert severity="error" sx={{ mb: 2 }}>Invalid or missing reset token</Alert>
                    <Link to="/forgot-password" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600 }}>
                        Request a new reset link
                    </Link>
                </Paper>
            </Container>
        );
    }

    return (
        <>
            <GlobalStyles
                styles={{
                    'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
                        '-webkit-box-shadow': `0 0 0 30px ${theme.palette.background.paper} inset !important`,
                        '-webkit-text-fill-color': `${theme.palette.text.primary} !important`,
                        'caret-color': `${theme.palette.text.primary} !important`,
                        'background-color': `${theme.palette.background.paper} !important`,
                        'transition': 'background-color 600000s 0s, color 600000s 0s !important'
                    }
                }}
            />
            <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3, md: 4 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%', borderRadius: 2, p: { xs: 3, sm: 4, md: 6 },
                        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.8) : theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
                        <Box
                            component="img"
                            src={logo}
                            alt="Market Rodeo Logo"
                            sx={{ height: 'auto', width: { xs: '60px', sm: '80px' }, transition: 'transform 0.3s ease-in-out', '&:hover': { transform: 'scale(1.05)' } }}
                        />

                        <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
                            <Typography
                                variant="h4"
                                gutterBottom
                                sx={{
                                    fontWeight: 800, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                                    background: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(to right, #fff 0%, #c7c7c7 100%)'
                                        : 'linear-gradient(to right, #1976d2 0%, #1565c0 100%)',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                }}
                            >
                                New Password
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                Enter your new password below
                            </Typography>
                        </Box>

                        {error && <Alert severity="error" sx={{ width: '100%', borderRadius: 1 }}>{error}</Alert>}
                        {message && (
                            <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <Alert severity="success" sx={{ width: '100%', borderRadius: 1, mb: 2 }}>{message}</Alert>
                                <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 600, fontSize: '1rem' }}>
                                    Go to Login
                                </Link>
                            </Box>
                        )}

                        {!message && (
                            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                <FormControl fullWidth variant="outlined" sx={{ mb: 2.5 }}>
                                    <InputLabel htmlFor="newPassword">New Password</InputLabel>
                                    <Input
                                        id="newPassword"
                                        type={showNewPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        sx={inputSx}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ color: alpha(theme.palette.primary.main, 0.7) }} />
                                            </InputAdornment>
                                        }
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end"
                                                    sx={{ transition: 'all 0.3s ease-in-out', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}>
                                                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>

                                <FormControl fullWidth variant="outlined" sx={{ mb: 2.5 }}>
                                    <InputLabel htmlFor="confirmPassword">Confirm Password</InputLabel>
                                    <Input
                                        id="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        sx={inputSx}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <LockIcon sx={{ color: alpha(theme.palette.primary.main, 0.7) }} />
                                            </InputAdornment>
                                        }
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end"
                                                    sx={{ transition: 'all 0.3s ease-in-out', '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) } }}>
                                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>

                                <Button
                                    fullWidth variant="contained" type="submit" disabled={loading}
                                    sx={{
                                        mt: 2, mb: 2, height: 48, textTransform: 'none', fontWeight: 600,
                                        fontSize: { xs: '0.875rem', sm: '1rem' }, borderRadius: 1.5,
                                        transition: 'all 0.3s ease-in-out',
                                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }
                                    }}
                                >
                                    {loading ? 'Resetting...' : 'Reset Password'}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Container>
        </>
    );
};

export default ResetPassword;